// src/app/services/shipment.service.ts

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {environment} from "../../environments/environment";
import {GeoAddress} from "../models/common/geo-address.model"; // Adjust path as needed
import { Shipment } from './models/shipment';
import {Page} from "../models/page.model";
import {ShipmentDelivery} from "./models/delivery/shipment-delivery.model";
import {ShipmentWaypoint} from "./models/waypoint/shipment.waypoint";
import {ShipmentDeliveryAttachment} from "./models/shipment-delivery.attachment";
import {ShipmentChargeLine, ShipmentChargeLineBulkCreateRequest, ShipmentChargeProductOption} from "./models/charge-line/shipment-charge-line";
import {ShipmentDeliveryActionData} from "./models/shipment-action-data.model";
import {ShipmentNominationPack} from "./models/nomination/shipment-nomination.pack";
import {Driver} from "../fleet/driver/driver.model";
import { TripCostSummary } from './models/trip-cost-summary';
import { ShipmentDeliveryStop, ShipmentDeliveryStopLine } from './models/workflow/shipment-delivery-stop.model';
import { ShipmentDeliveryOperationStatus, ShipmentEvidence, ShipmentWorkflowEvent, ShipmentWorkflowTransitionRequest } from './models/workflow/shipment-workflow.model';
import { ShipmentRemark } from './models/shipment-remark.model';
import { ShipmentRemarksFeed } from './models/shipment-remarks-feed.model';
import { ShipmentWaybillSummary } from './models/shipment-waybill-summary.model';
import { PayloadItemView } from '../models/meta-params/payload-item-view';
import { ContainerCase } from '../container-tracking/models/container-case.model';

export interface ShipmentContainerOption {
    id: number;
    containerNumber?: string;
    caseNumber: string;
    deliveryId?: number;
}
import { DeliveryGpsTrackingSnapshot } from '../fleet/gps-tracking/gps-tracking.model';
import {
    DeliveryDirectoryRow,
    DirectoryOptionPage,
    DirectoryPage,
    ShipmentDirectoryRow
} from './models/shipment-directory.model';

export interface ShipmentCommercialPartyCorrectionRequest {
    target: {
        source: 'CRM';
        crmClientId: number;
        crmAccountId: number | null;
        crmContactId: number | null;
    };
    expected: {
        crmClientId: number | null;
        crmAccountId: number | null;
        crmContactId: number | null;
        legacyCompanyId: number | null;
        legacyCustomerId: number | null;
    };
    reason: string;
    confirmPreserveCommercials: boolean;
}

export interface ShipmentConsolidationRelease {
    releaseId: number;
    releaseNumber: string;
    sourceType?: string;
    sourceNumber?: string;
    projectId?: number;
    projectNumber?: string;
    status?: string;
    originName?: string;
    destinationName?: string;
}

export interface ShipmentConsolidation {
    shipmentId: number;
    billingMode: string;
    sourceStatus: 'SNAPSHOT' | 'RECONSTRUCTED' | 'UNAVAILABLE' | 'NOT_APPLICABLE';
    clientCount: number;
    partyCount: number;
    releaseCount: number;
    clients: Array<{
        key: string;
        partySource: 'CRM' | 'LEGACY';
        clientId?: number;
        name: string;
        releaseCount: number;
        parties: Array<{
            key: string;
            accountId?: number;
            customerId?: number;
            name: string;
            releases: ShipmentConsolidationRelease[];
        }>;
    }>;
}

export interface ShipmentScheduleCorrectionRequest {
    pickupTime: number;
    dropoffTime: number;
    expectedPickupTime: number | null;
    expectedDropoffTime: number | null;
    reason: string;
}

export interface ReassignDeliveryCrewAssetRequest {
    driverId?: number | null;
    driverResourceCompanyId?: number | null;
    vehicleId?: number | null;
    vehicleResourceCompanyId?: number | null;
    transportCostAmount?: number | null;
    transportCostCurrencyCode?: string;
    actorCustomerId?: number;
    reason?: string;
    noteHtml: string;
}

export interface DeliveryFinanceReconciliationRow {
    deliveryId: number;
    deliveryNumber: string;
    expectedChannel?: string;
    expectedPayeeType?: string;
    expectedPayeeId?: number;
    expectedPayeeName?: string;
    capturedChannel?: string;
    capturedTransportCost?: number;
    currencyCode?: string;
    autoTransportChargeBuyingAmount?: number;
    payableIds: number[];
    bulkCostIds: number[];
    mismatchStatus: string;
    mismatchDetail: string;
}

export interface DeliveryFinanceReconciliation {
    shipmentId: number;
    shipmentNumber: string;
    posted: boolean;
    deliveries: DeliveryFinanceReconciliationRow[];
}

export interface DeliveryFinanceRepairResult {
    shipmentId: number;
    deliveryId: number;
    repaired: boolean;
    status: string;
    message: string;
}

export interface CancelShipmentDeliveryRequest {
    reason: string;
    actorCustomerId?: number;
}

export interface HistoricalDeliveryCompletionRequest {
    completedAt: number;
    actorCustomerId: number;
    overrideReason?: string;
}

export type ShipmentDeliveryReleaseAmendmentStatus =
    | 'PREVIEWED' | 'APPLYING' | 'COMPLETED' | 'FAILED' | 'COMPLIANCE_PENDING' | 'EXPIRED';

export interface ShipmentDeliveryReleaseAmendmentRouteStop {
    sequence: number;
    stopId?: number;
    locationId?: number;
    locationName?: string;
    type: 'PICKUP' | 'DROPOFF' | string;
    releaseId?: number;
    releaseNumber?: string;
    plannedArrival?: string | Date;
    plannedDeparture?: string | Date;
}

export interface ShipmentDeliveryReleaseAmendment {
    amendmentId: number;
    shipmentId: number;
    deliveryId: number;
    status: ShipmentDeliveryReleaseAmendmentStatus;
    releaseIds: number[];
    route: ShipmentDeliveryReleaseAmendmentRouteStop[];
    metrics?: { distanceKm?: number; durationMinutes?: number; totalWeightKg?: number; totalVolumeCbm?: number; totalPallets?: number; };
    prices?: { currentBuyingPrice?: number; currentSellingPrice?: number; proposedBuyingPrice?: number; proposedSellingPrice?: number; buyingDelta?: number; sellingDelta?: number; };
    requiresZeroSellConversion?: boolean;
    timingWarning?: string;
    dangerWarnings?: string[];
    reason?: string;
    previousGovtWaybillId?: number;
    replacementGovtWaybillId?: number;
    bayanError?: string;
    expiresAt?: string | Date;
    appliedAt?: string | Date;
}

export interface ShipmentDeliveryReleaseAmendmentPreviewRequest {
    releaseIds: number[];
    actorCustomerId?: number;
}

export interface ShipmentDeliveryReleaseAmendmentApplyRequest {
    reason: string;
    confirmedBuyingPrice: number;
    confirmedSellingPrice: number;
    confirmZeroSellConversion?: boolean;
    actorCustomerId?: number;
}

export interface ShipmentReleaseCandidate {
    releaseId: number;
    releaseNumber: string;
    sourceNumber?: string;
    originName?: string;
    destinationName?: string;
    vehicleTypeName?: string;
    crmClientId?: number;
    crmClientName?: string;
    crmAccountId?: number;
    crmAccountName?: string;
    partyKey?: string;
    fitPercentage: number;
    requiresZeroSellConversion: boolean;
    timingWarning?: string;
    dangerWarnings?: string[];
    blockerReason?: string;
}

export interface ShipmentReleaseCandidatePage {
    items: ShipmentReleaseCandidate[];
    totalElements: number;
    page: number;
    pageSize: number;
    totalPages: number;
}

export interface ShipmentReleaseAttachmentPreview {
    attachmentId: number;
    releaseIds: number[];
    status: string;
    route: ShipmentDeliveryReleaseAmendmentRouteStop[];
    metrics?: { distanceKm?: number; durationMinutes?: number; totalWeightKg?: number; totalPallets?: number };
    requiresZeroSellConversion: boolean;
    timingWarning?: string;
}

export interface ShipmentReleaseAttachmentRequest {
    releaseIds: number[];
    actorCustomerId?: number;
}

export interface ShipmentReleaseAttachmentResult {
    shipmentId: number;
    releaseIds: number[];
    attachedCount: number;
    alreadyAttachedCount: number;
}

export interface ShipmentPayloadExtractionResult {
    scope: 'SHIPMENT' | 'DELIVERY';
    shipmentId: number;
    deliveryId?: number;
    customerOrderNumber?: string;
    attachment?: ShipmentDeliveryAttachment;
    payloadItemList: PayloadItemView[];
}

export interface ShipmentPayloadLineCommand {
    lineSource: 'GENERAL_CARGO' | 'WAREHOUSE_ITEM';
    payloadItem: PayloadItemView;
}

export interface ShipmentContainerManifestCommand {
    containerNumber?: string;
    isoEquipmentType?: string;
    sealNumber?: string;
    bookingNumber?: string;
    billOfLadingNumber?: string;
    houseBillNumber?: string;
    customsDeclarationNumber?: string;
    journeyTemplateId?: number;
}

export interface ShipmentPayloadLineCommandResult {
    payloadItems: PayloadItemView[];
    warehouseDocumentType?: 'DISPATCH' | 'TRANSFER';
    warehouseDocumentId?: number;
    warehouseDocumentNumber?: string;
    containerCase?: ContainerCase;
}

export interface DeliveryLineRejectionRequest {
    acceptedQuantity: number;
    rejectedQuantity: number;
    reasonCode: string;
    remarks?: string;
}

export interface DeliveryLineRejectionResult {
    sourceLineId: number;
    returnStopId: number;
    returnLineId: number;
    acceptedQuantity: number;
    rejectedQuantity: number;
    destinationConfirmed: boolean;
}

export interface DeliverySiteReason {
    reasonId: number;
    actionType: 'REJECT' | 'RESCHEDULE' | 'SKIP';
    reasonCode: string;
    label: string;
    active: boolean;
    sortOrder: number;
    companyManaged: boolean;
}

export interface DeliverySiteDecisionRequest {
    action: 'REJECT' | 'RESCHEDULE';
    scope: 'FULL' | 'PARTIAL';
    quantity?: number;
    reasonCode: string;
    remarks?: string;
    allocations?: Array<{ stopLineId: number; quantity: number }>;
    requestId?: string;
}

export interface DeliverySiteDecisionResult {
    sourceLineId: number;
    action: string;
    decidedQuantity: number;
    acceptedQuantity: number;
    returnStopId?: number;
    returnLineId?: number;
    destinationConfirmed: boolean;
}

export interface WaypointRouteSegment {
    sequence: number;
    fromWaypointId?: number;
    toWaypointId?: number;
    fromWaypointNumber?: number;
    toWaypointNumber?: number;
    fromName?: string;
    toName?: string;
    distanceKm?: number;
    durationSeconds?: number;
    routeAvailable?: boolean;
}

export interface ShipmentAccountsPostingResult {
    shipmentId: number;
    shipmentNumber: string;
    shipmentStatus: string;
    bulkCostIds: number[];
    receivableIds: number[];
    payableIds: number[];
    channelOutcomes: Record<string, string>;
}
export interface ShipmentAccountsPostingPreview {
    shipmentId: number; owningCompanyId: number; shipmentNumber: string; currencyCode: string;
    customerSellAmount: number; deliveryTransportCost: number; workerEarnedPay: number;
    lines: Array<{ category: string; reference: string; status: string; currencyCode: string; amount?: number; detail: string }>;
    blockers: string[];
    internalCostLines?: Array<{ deliveryReference: string; chargeType: string; description: string; currencyCode: string; amount: number }>;
    internalCostTotals?: Array<{ currencyCode: string; amount: number }>;
}

@Injectable({
    providedIn: 'root'
})
export class ShipmentService {

    private readonly apiUrl = `${environment.restApiUrl}shipments`;

    constructor(private http: HttpClient) { }

    // --- Geo Endpoints ---
    getGeoAddress(latitude: number, longitude: number, cultureCode: string): Observable<GeoAddress> {
        const params = new HttpParams()
            .set('latitude', latitude.toString())
            .set('longitude', longitude.toString())
            .set('cultureCode', cultureCode);
        return this.http.get<GeoAddress>(`${this.apiUrl}/geo-address`, { params });
    }

    // --- Shipment Header Endpoints ---
    createShipment(shipment: Shipment): Observable<Shipment> {
        return this.http.post<Shipment>(this.apiUrl, shipment);
    }

    getShipmentById(shipmentId: number, cultureCode: string = 'en-US'): Observable<Shipment> {
        const params = new HttpParams().set('cultureCode', cultureCode);
        return this.http.get<Shipment>(`${this.apiUrl}/${shipmentId}`, { params });
    }

    getShipmentContainers(shipmentId: number): Observable<ShipmentContainerOption[]> {
        return this.http.get<ShipmentContainerOption[]>(`${this.apiUrl}/${shipmentId}/container-cases`);
    }

    assignShipmentContainers(shipmentId: number, deliveryId: number, caseIds: number[]): Observable<void> {
        return this.http.post<void>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/container-cases`, caseIds);
    }

    getShipmentConsolidation(shipmentId: number): Observable<ShipmentConsolidation> {
        return this.http.get<ShipmentConsolidation>(`${this.apiUrl}/${shipmentId}/consolidation`);
    }

    searchShipments(searchParams: { [param: string]: string }, pageable: { page: number, size: number, sort?: string }): Observable<Page<Shipment>> {
        let params = new HttpParams()
            .set('page', pageable.page.toString())
            .set('size', pageable.size.toString());
        if (pageable.sort) {
            params = params.set('sort', pageable.sort);
        }
        for (const key in searchParams) {
            if (searchParams.hasOwnProperty(key)) {
                params = params.set(key, searchParams[key]);
            }
        }
        return this.http.get<Page<Shipment>>(this.apiUrl, { params });
    }

    searchDeliveries(searchParams: { [param: string]: string }, pageable: { page: number, size: number, sort?: string }): Observable<Page<ShipmentDelivery>> {
        let params = new HttpParams()
            .set('page', pageable.page.toString())
            .set('size', pageable.size.toString());
        if (pageable.sort) {
            params = params.set('sort', pageable.sort);
        }
        for (const key in searchParams) {
            if (searchParams.hasOwnProperty(key)) {
                params = params.set(key, searchParams[key]);
            }
        }
        return this.http.get<Page<ShipmentDelivery>>(`${this.apiUrl}/deliveries/search`, { params });
    }

    searchShipmentDirectory(searchParams: { [param: string]: string }, pageable: { page: number, size: number, sort?: string }): Observable<DirectoryPage<ShipmentDirectoryRow>> {
        return this.http.get<DirectoryPage<ShipmentDirectoryRow>>(`${this.apiUrl}/directory`, {
            params: this.directoryParams(searchParams, pageable)
        });
    }

    searchDeliveryDirectory(searchParams: { [param: string]: string }, pageable: { page: number, size: number, sort?: string }): Observable<DirectoryPage<DeliveryDirectoryRow>> {
        return this.http.get<DirectoryPage<DeliveryDirectoryRow>>(`${this.apiUrl}/directory/deliveries`, {
            params: this.directoryParams(searchParams, pageable)
        });
    }

    getDirectoryOptions(type: 'clients' | 'legacyCompanies' | 'legacyCustomers' | 'crmClients' | 'crmAccounts' | 'crmContacts' | 'drivers' | 'vehicles' | 'projects', owningCompanyId: number,
        search = '', page = 0, size = 25): Observable<DirectoryOptionPage> {
        let params = new HttpParams()
            .set('owningCompanyId', owningCompanyId.toString())
            .set('page', page.toString())
            .set('size', size.toString());
        if (search.trim()) {
            params = params.set('search', search.trim());
        }
        return this.http.get<DirectoryOptionPage>(`${this.apiUrl}/directory/options/${type}`, { params });
    }

    private directoryParams(searchParams: { [param: string]: string }, pageable: { page: number, size: number, sort?: string }): HttpParams {
        let params = new HttpParams()
            .set('page', pageable.page.toString())
            .set('size', pageable.size.toString());
        if (pageable.sort) {
            params = params.set('sort', pageable.sort);
        }
        Object.entries(searchParams).forEach(([key, value]) => {
            if (value != null && value !== '') {
                params = params.set(key, value);
            }
        });
        return params;
    }

    // Full shipment replacement is intentionally disabled. These focused calls
    // remain available for approved operational adjustments.
    updateRequestedTruckCount(shipmentId: number, requestedTruckCount: number): Observable<void> {
        return this.http.patch<void>(`${this.apiUrl}/${shipmentId}/requested-truck-count`, { requestedTruckCount });
    }

    correctCommercialParty(shipmentId: number, request: ShipmentCommercialPartyCorrectionRequest): Observable<void> {
        return this.http.post<void>(`${this.apiUrl}/${shipmentId}/commercial-party-correction`, request);
    }

    correctSchedule(shipmentId: number, request: ShipmentScheduleCorrectionRequest): Observable<void> {
        return this.http.patch<void>(`${this.apiUrl}/${shipmentId}/schedule`, request);
    }

    changeShipmentStatus(shipmentId: number, status: string): Observable<void> {
        const payload = { 'status': status };
        return this.http.patch<void>(`${this.apiUrl}/${shipmentId}/status`, payload);
    }

    postShipmentToAccounts(shipmentId: number): Observable<ShipmentAccountsPostingResult> {
        return this.http.post<ShipmentAccountsPostingResult>(
            `${this.apiUrl}/${shipmentId}/post-to-accounts`,
            {}
        );
    }
    accountsPostingPreview(shipmentId: number): Observable<ShipmentAccountsPostingPreview> {
        return this.http.get<ShipmentAccountsPostingPreview>(`${this.apiUrl}/${shipmentId}/accounts-posting-preview`);
    }

    deleteShipment(shipmentId: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${shipmentId}`);
    }

    // --- Delivery Endpoints ---
    createDelivery(shipmentId: number, delivery: ShipmentDelivery): Observable<ShipmentDelivery> {
        return this.http.post<ShipmentDelivery>(`${this.apiUrl}/${shipmentId}/deliveries`, delivery);
    }

    getDeliveriesForShipment(shipmentId: number): Observable<ShipmentDelivery[]> {
        return this.http.get<ShipmentDelivery[]>(`${this.apiUrl}/${shipmentId}/deliveries`);
    }

    updateDelivery(shipmentId: number, deliveryId: number, delivery: ShipmentDelivery): Observable<ShipmentDelivery> {
        return this.http.put<ShipmentDelivery>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}`, delivery);
    }

    reassignDeliveryCrewAsset(shipmentId: number, deliveryId: number, request: ReassignDeliveryCrewAssetRequest): Observable<ShipmentDelivery> {
        return this.http.patch<ShipmentDelivery>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/crew-asset`, request);
    }

    getDeliveryFinanceReconciliation(shipmentId: number): Observable<DeliveryFinanceReconciliation> {
        return this.http.get<DeliveryFinanceReconciliation>(`${this.apiUrl}/${shipmentId}/delivery-finance-reconciliation`);
    }

    repairDeliveryFinance(shipmentId: number, deliveryId: number): Observable<DeliveryFinanceRepairResult> {
        return this.http.post<DeliveryFinanceRepairResult>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/repair-finance`, {});
    }

    cancelDelivery(shipmentId: number, deliveryId: number, request: CancelShipmentDeliveryRequest): Observable<ShipmentDelivery> {
        return this.http.post<ShipmentDelivery>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/cancel`, request);
    }

    previewDeliveryReleaseAmendment(shipmentId: number, deliveryId: number,
                                    request: ShipmentDeliveryReleaseAmendmentPreviewRequest): Observable<ShipmentDeliveryReleaseAmendment> {
        return this.http.post<ShipmentDeliveryReleaseAmendment>(
            `${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/release-amendments/preview`, request);
    }

    attachOrderReleases(shipmentId: number,
                        request: ShipmentReleaseAttachmentRequest): Observable<ShipmentReleaseAttachmentResult> {
        return this.http.post<ShipmentReleaseAttachmentResult>(`${this.apiUrl}/${shipmentId}/order-releases`, request);
    }

    getOrderReleaseCandidates(shipmentId: number, deliveryId?: number, search = '', page = 0): Observable<ShipmentReleaseCandidatePage> {
        let params = new HttpParams().set('page', page).set('pageSize', 25);
        if (deliveryId != null) params = params.set('deliveryId', deliveryId);
        if (search.trim()) params = params.set('search', search.trim());
        return this.http.get<ShipmentReleaseCandidatePage>(`${this.apiUrl}/${shipmentId}/order-release-candidates`, { params });
    }

    previewOrderReleaseAttachment(shipmentId: number, request: ShipmentReleaseAttachmentRequest): Observable<ShipmentReleaseAttachmentPreview> {
        return this.http.post<ShipmentReleaseAttachmentPreview>(`${this.apiUrl}/${shipmentId}/order-releases/preview`, request);
    }

    applyOrderReleaseAttachment(shipmentId: number, attachmentId: number, confirmZeroSellConversion: boolean): Observable<ShipmentReleaseAttachmentPreview> {
        return this.http.post<ShipmentReleaseAttachmentPreview>(
            `${this.apiUrl}/${shipmentId}/order-releases/${attachmentId}/apply`, { confirmZeroSellConversion });
    }

    applyDeliveryReleaseAmendment(shipmentId: number, deliveryId: number, amendmentId: number,
                                  request: ShipmentDeliveryReleaseAmendmentApplyRequest): Observable<ShipmentDeliveryReleaseAmendment> {
        return this.http.post<ShipmentDeliveryReleaseAmendment>(
            `${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/release-amendments/${amendmentId}/apply`, request);
    }

    getDeliveryReleaseAmendments(shipmentId: number, deliveryId: number): Observable<ShipmentDeliveryReleaseAmendment[]> {
        return this.http.get<ShipmentDeliveryReleaseAmendment[]>(
            `${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/release-amendments`);
    }

    retryDeliveryReleaseAmendmentBayan(shipmentId: number, deliveryId: number, amendmentId: number): Observable<ShipmentDeliveryReleaseAmendment> {
        return this.http.post<ShipmentDeliveryReleaseAmendment>(
            `${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/release-amendments/${amendmentId}/retry`, {});
    }

    changeDeliveryStatus(shipmentId: number, deliveryId: number, status: string): Observable<void> {
        const payload = { 'status': status };
        return this.http.patch<void>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/status`, payload);
    }

    getDeliveryStops(shipmentId: number, deliveryId: number): Observable<ShipmentDeliveryStop[]> {
        return this.http.get<ShipmentDeliveryStop[]>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops`);
    }

    createDeliveryStop(shipmentId: number, deliveryId: number, stop: ShipmentDeliveryStop): Observable<ShipmentDeliveryStop> {
        return this.http.post<ShipmentDeliveryStop>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops`, stop);
    }

    updateDeliveryStop(shipmentId: number, deliveryId: number, stopId: number, stop: ShipmentDeliveryStop): Observable<ShipmentDeliveryStop> {
        return this.http.put<ShipmentDeliveryStop>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops/${stopId}`, stop);
    }

    createDeliveryOnlyStop(shipmentId: number, deliveryId: number, request: { locationId: number; remarks?: string }): Observable<ShipmentDeliveryStop> {
        return this.http.post<ShipmentDeliveryStop>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/delivery-only-stops`, request);
    }

    updateDeliveryOnlyStop(shipmentId: number, deliveryId: number, stopId: number, request: { locationId: number; remarks?: string }): Observable<ShipmentDeliveryStop> {
        return this.http.put<ShipmentDeliveryStop>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/delivery-only-stops/${stopId}`, request);
    }

    deleteDeliveryOnlyStop(shipmentId: number, deliveryId: number, stopId: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/delivery-only-stops/${stopId}`);
    }

    replaceDeliveryStopLines(shipmentId: number, deliveryId: number, stopId: number, lineItems: ShipmentDeliveryStopLine[]): Observable<ShipmentDeliveryStopLine[]> {
        return this.http.put<ShipmentDeliveryStopLine[]>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops/${stopId}/lines`, lineItems);
    }

    replaceDeliveryRouteStopLines(shipmentId: number, deliveryId: number, lineItems: ShipmentDeliveryStopLine[]): Observable<ShipmentDeliveryStop[]> {
        return this.http.put<ShipmentDeliveryStop[]>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops/lines`, lineItems);
    }

    seedDeliveryStopsFromWaypoints(shipmentId: number, deliveryId: number): Observable<ShipmentDeliveryStop[]> {
        return this.http.post<ShipmentDeliveryStop[]>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops/seed-from-waypoints`, {});
    }

    transitionShipmentWorkflow(shipmentId: number, request: ShipmentWorkflowTransitionRequest): Observable<ShipmentWorkflowEvent> {
        return this.http.post<ShipmentWorkflowEvent>(`${this.apiUrl}/${shipmentId}/workflow/transitions`, request);
    }

    transitionDeliveryWorkflow(shipmentId: number, deliveryId: number, request: ShipmentWorkflowTransitionRequest): Observable<ShipmentWorkflowEvent> {
        return this.http.post<ShipmentWorkflowEvent>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/workflow/transitions`, request);
    }

    completeDeliveryHistorically(
        shipmentId: number,
        deliveryId: number,
        request: HistoricalDeliveryCompletionRequest
    ): Observable<ShipmentWorkflowEvent> {
        return this.http.post<ShipmentWorkflowEvent>(
            `${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/historical-completion`,
            request
        );
    }

    transitionDeliveryStopWorkflow(shipmentId: number, deliveryId: number, stopId: number, request: ShipmentWorkflowTransitionRequest): Observable<ShipmentWorkflowEvent> {
        return this.http.post<ShipmentWorkflowEvent>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops/${stopId}/workflow/transitions`, request);
    }

    transitionDeliveryStopLineWorkflow(shipmentId: number, deliveryId: number, stopId: number, stopLineId: number, request: ShipmentWorkflowTransitionRequest): Observable<ShipmentWorkflowEvent> {
        return this.http.post<ShipmentWorkflowEvent>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops/${stopId}/lines/${stopLineId}/workflow/transitions`, request);
    }

    transitionPayloadLineWorkflow(shipmentId: number, deliveryId: number, deliveryLineId: number, request: ShipmentWorkflowTransitionRequest): Observable<ShipmentWorkflowEvent> {
        return this.http.post<ShipmentWorkflowEvent>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/payload-lines/${deliveryLineId}/workflow/transitions`, request);
    }

    getShipmentWorkflowTimeline(shipmentId: number, includeChildren: boolean = true): Observable<ShipmentWorkflowEvent[]> {
        const params = new HttpParams().set('includeChildren', String(includeChildren));
        return this.http.get<ShipmentWorkflowEvent[]>(`${this.apiUrl}/${shipmentId}/workflow/timeline`, { params });
    }

    getDeliveryWorkflowTimeline(shipmentId: number, deliveryId: number): Observable<ShipmentWorkflowEvent[]> {
        return this.http.get<ShipmentWorkflowEvent[]>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/workflow/timeline`);
    }

    getDeliveryOperationStatus(shipmentId: number, deliveryId: number): Observable<ShipmentDeliveryOperationStatus> {
        return this.http.get<ShipmentDeliveryOperationStatus>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/operation-status`);
    }

    getDeliveryGpsTracking(shipmentId: number, deliveryId: number, sourceKey?: string): Observable<DeliveryGpsTrackingSnapshot> {
        let params = new HttpParams();
        if (sourceKey) {
            params = params.set('sourceKey', sourceKey);
        }
        return this.http.get<DeliveryGpsTrackingSnapshot>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/gps-tracking`, { params });
    }

    addDeliveryEvidence(shipmentId: number, deliveryId: number, evidence: ShipmentEvidence): Observable<ShipmentEvidence> {
        return this.http.post<ShipmentEvidence>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/evidence`, evidence);
    }

    getDeliveryEvidence(shipmentId: number, deliveryId: number): Observable<ShipmentEvidence[]> {
        return this.http.get<ShipmentEvidence[]>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/evidence`);
    }

    rejectDeliveryLine(shipmentId: number, deliveryId: number, stopId: number, lineId: number,
        request: DeliveryLineRejectionRequest): Observable<DeliveryLineRejectionResult> {
        return this.http.post<DeliveryLineRejectionResult>(
            `${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops/${stopId}/lines/${lineId}/rejection`, request);
    }

    getDeliverySiteReasons(companyId: number, actionType: 'REJECT' | 'RESCHEDULE' | 'SKIP',
        includeInactive = false): Observable<DeliverySiteReason[]> {
        return this.http.get<DeliverySiteReason[]>(`${environment.restApiUrl}delivery-site-reasons/companies/${companyId}`,
            { params: { actionType, includeInactive } });
    }

    saveDeliverySiteReason(companyId: number, reasonId: number | null,
        reason: { actionType: string; reasonCode: string; label: string; active: boolean; sortOrder: number }): Observable<DeliverySiteReason> {
        const url = `${environment.restApiUrl}delivery-site-reasons/companies/${companyId}`;
        return reasonId ? this.http.put<DeliverySiteReason>(`${url}/${reasonId}`, reason)
            : this.http.post<DeliverySiteReason>(url, reason);
    }

    decideDeliverySiteLine(shipmentId: number, deliveryId: number, stopId: number, lineId: number,
        request: DeliverySiteDecisionRequest): Observable<DeliverySiteDecisionResult> {
        return this.http.post<DeliverySiteDecisionResult>(
            `${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops/${stopId}/lines/${lineId}/decision`, request);
    }

    decideDeliverySite(shipmentId: number, deliveryId: number, stopId: number,
        request: { action: 'REJECT' | 'RESCHEDULE'; reasonCode: string; remarks?: string;
            allocations?: Array<{ sourceLineId: number; stopLineId: number; quantity: number }> }): Observable<unknown> {
        return this.http.post(
            `${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops/${stopId}/decision`, request);
    }

    skipDeliverySite(shipmentId: number, deliveryId: number, stopId: number,
        request: { reasonCode: string; afterStopId?: number }): Observable<unknown> {
        return this.http.post(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops/${stopId}/skip`, request);
    }

    confirmRejectionReturn(shipmentId: number, deliveryId: number, stopId: number, locationId: number): Observable<ShipmentDeliveryStop> {
        return this.http.post<ShipmentDeliveryStop>(
            `${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/rejection-return/${stopId}/confirm`, { locationId });
    }

    // --- Waypoint Endpoints ---
    getWaypointsForShipment(shipmentId: number, cultureCode: string = 'en-US'): Observable<ShipmentWaypoint[]> {
        const params = new HttpParams().set('cultureCode', cultureCode);
        return this.http.get<ShipmentWaypoint[]>(`${this.apiUrl}/${shipmentId}/waypoints`, { params });
    }

    getWaypointRouteSegments(shipmentId: number, cultureCode: string = 'en-US'): Observable<WaypointRouteSegment[]> {
        const params = new HttpParams().set('cultureCode', cultureCode);
        return this.http.get<WaypointRouteSegment[]>(`${this.apiUrl}/${shipmentId}/waypoints/route-segments`, { params });
    }

    createWaypoint(shipmentId: number, waypoint: ShipmentWaypoint): Observable<ShipmentWaypoint> {
        return this.http.post<ShipmentWaypoint>(`${this.apiUrl}/${shipmentId}/waypoints`, waypoint);
    }

    updateWaypoint(shipmentId: number, waypointId: number, waypoint: ShipmentWaypoint): Observable<ShipmentWaypoint> {
        return this.http.put<ShipmentWaypoint>(`${this.apiUrl}/${shipmentId}/waypoints/${waypointId}`, waypoint);
    }

    replaceWaypoints(shipmentId: number, waypoints: ShipmentWaypoint[], overrideReason?: string, actorCustomerId?: number): Observable<ShipmentWaypoint[]> {
        let params = new HttpParams();
        if (overrideReason) {
            params = params.set('overrideReason', overrideReason);
        }
        if (actorCustomerId != null) {
            params = params.set('actorCustomerId', String(actorCustomerId));
        }
        return this.http.put<ShipmentWaypoint[]>(`${this.apiUrl}/${shipmentId}/waypoints`, waypoints, { params });
    }

    deleteWaypoint(shipmentId: number, waypointId: number, overrideReason?: string, actorCustomerId?: number): Observable<void> {
        let params = new HttpParams();
        if (overrideReason) {
            params = params.set('overrideReason', overrideReason);
        }
        if (actorCustomerId != null) {
            params = params.set('actorCustomerId', String(actorCustomerId));
        }
        return this.http.delete<void>(`${this.apiUrl}/${shipmentId}/waypoints/${waypointId}`, { params });
    }

    // --- Attachment Endpoints ---
    getShipmentAttachments(shipmentId: number): Observable<ShipmentDeliveryAttachment[]> {
        return this.http.get<ShipmentDeliveryAttachment[]>(`${this.apiUrl}/${shipmentId}/attachments`);
    }

    addShipmentAttachments(shipmentId: number, attachments: ShipmentDeliveryAttachment[]): Observable<void> {
        return this.http.post<void>(`${this.apiUrl}/${shipmentId}/attachments`, attachments);
    }

    extractShipmentPayload(shipmentId: number, file: File, fileType: string = 'AI Payload Document'): Observable<ShipmentPayloadExtractionResult> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', fileType);
        return this.http.post<ShipmentPayloadExtractionResult>(`${this.apiUrl}/${shipmentId}/payload-extraction`, formData);
    }

    createShipmentPayloadLine(shipmentId: number, command: ShipmentPayloadLineCommand): Observable<ShipmentPayloadLineCommandResult> {
        return this.http.post<ShipmentPayloadLineCommandResult>(`${this.apiUrl}/${shipmentId}/payload-lines`, command);
    }

    updateShipmentPayloadLine(shipmentId: number, shipmentLineId: number, command: ShipmentPayloadLineCommand): Observable<ShipmentPayloadLineCommandResult> {
        return this.http.put<ShipmentPayloadLineCommandResult>(`${this.apiUrl}/${shipmentId}/payload-lines/${shipmentLineId}`, command);
    }

    deleteShipmentPayloadLine(shipmentId: number, shipmentLineId: number): Observable<ShipmentPayloadLineCommandResult> {
        return this.http.delete<ShipmentPayloadLineCommandResult>(`${this.apiUrl}/${shipmentId}/payload-lines/${shipmentLineId}`);
    }

    createShipmentContainerManifest(shipmentId: number, command: ShipmentContainerManifestCommand): Observable<ShipmentPayloadLineCommandResult> {
        return this.http.post<ShipmentPayloadLineCommandResult>(`${this.apiUrl}/${shipmentId}/container-manifests`, command);
    }

    getDeliveryAttachments(shipmentId: number, deliveryId: number): Observable<ShipmentDeliveryAttachment[]> {
        return this.http.get<ShipmentDeliveryAttachment[]>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/attachments`);
    }

    getDeliveryStopAttachments(shipmentId: number, deliveryId: number, stopId: number): Observable<ShipmentDeliveryAttachment[]> {
        return this.http.get<ShipmentDeliveryAttachment[]>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops/${stopId}/attachments`);
    }

    addDeliveryAttachments(shipmentId: number, deliveryId: number, attachments: ShipmentDeliveryAttachment[]): Observable<void> {
        return this.http.post<void>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/attachments`, attachments);
    }

    extractDeliveryPayload(shipmentId: number, deliveryId: number, file: File, fileType: string = 'AI Payload Document'): Observable<ShipmentPayloadExtractionResult> {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('fileType', fileType);
        return this.http.post<ShipmentPayloadExtractionResult>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/payload-extraction`, formData);
    }

    createDeliveryPayloadLine(shipmentId: number, deliveryId: number, command: ShipmentPayloadLineCommand): Observable<ShipmentPayloadLineCommandResult> {
        return this.http.post<ShipmentPayloadLineCommandResult>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/payload-lines`, command);
    }

    updateDeliveryPayloadLine(shipmentId: number, deliveryId: number, shipmentLineId: number, command: ShipmentPayloadLineCommand): Observable<ShipmentPayloadLineCommandResult> {
        return this.http.put<ShipmentPayloadLineCommandResult>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/payload-lines/${shipmentLineId}`, command);
    }

    deleteDeliveryPayloadLine(shipmentId: number, deliveryId: number, shipmentLineId: number): Observable<ShipmentPayloadLineCommandResult> {
        return this.http.delete<ShipmentPayloadLineCommandResult>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/payload-lines/${shipmentLineId}`);
    }

    createDeliveryContainerManifest(shipmentId: number, deliveryId: number, command: ShipmentContainerManifestCommand): Observable<ShipmentPayloadLineCommandResult> {
        return this.http.post<ShipmentPayloadLineCommandResult>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/container-manifests`, command);
    }

    regenerateDeliveryWaybills(shipmentId: number, deliveryId: number): Observable<ShipmentDeliveryAttachment[]> {
        return this.http.post<ShipmentDeliveryAttachment[]>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/waybills/regenerate`, {});
    }

    generateDeliveryNote(shipmentId: number, deliveryId: number): Observable<ShipmentDeliveryAttachment> {
        return this.http.post<ShipmentDeliveryAttachment>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/delivery-note/generate`, {});
    }

    generateDeliveryNotes(shipmentId: number, deliveryId: number): Observable<ShipmentDeliveryAttachment[]> {
        return this.http.post<ShipmentDeliveryAttachment[]>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/delivery-notes/generate`, {});
    }

    deleteAttachment(attachmentId: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/attachments/${attachmentId}`);
    }

    // --- Charge Line Endpoints ---
    getChargeLinesForShipment(shipmentId: number): Observable<ShipmentChargeLine[]> {
        return this.http.get<ShipmentChargeLine[]>(`${this.apiUrl}/${shipmentId}/charges`);
    }

    addOrUpdateChargeLine(shipmentId: number, chargeLine: ShipmentChargeLine): Observable<ShipmentChargeLine> {
        return this.http.post<ShipmentChargeLine>(`${this.apiUrl}/${shipmentId}/charges`, chargeLine);
    }

    addChargeLines(shipmentId: number, request: ShipmentChargeLineBulkCreateRequest): Observable<ShipmentChargeLine[]> {
        return this.http.post<ShipmentChargeLine[]>(`${this.apiUrl}/${shipmentId}/charges/bulk`, request);
    }

    getChargeProducts(owningCompanyId: number, currencyCode: string): Observable<ShipmentChargeProductOption[]> {
        const params = new HttpParams()
            .set('owningCompanyId', owningCompanyId)
            .set('currencyCode', currencyCode);
        return this.http.get<ShipmentChargeProductOption[]>(`${this.apiUrl}/charge-products`, { params });
    }

    deleteChargeLine(shipmentId: number, chargeLineId: number): Observable<void> {
        return this.http.delete<void>(`${this.apiUrl}/${shipmentId}/charges/${chargeLineId}`);
    }

    // --- Delivery Action Data Endpoints ---
    getDeliveryActionData(shipmentId: number, deliveryId: number): Observable<ShipmentDeliveryActionData> {
        return this.http.get<ShipmentDeliveryActionData>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/action-data`);
    }

    saveDeliveryActionData(shipmentId: number, deliveryId: number, actionData: ShipmentDeliveryActionData): Observable<void> {
        return this.http.put<void>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/action-data`, actionData);
    }

    // --- Delivery Status History Endpoints ---
    getDeliveryStatusHistory(shipmentId: number, deliveryId: number): Observable<Map<string, number>> {
        return this.http.get<Map<string, number>>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/status-history`);
    }

    // --- Nomination Endpoints ---
    getShipmentNominations(shipmentId: number): Observable<ShipmentNominationPack> {
        return this.http.get<ShipmentNominationPack>(`${this.apiUrl}/${shipmentId}/nominations`);
    }

    saveShipmentNominations(shipmentId: number, nominationPack: ShipmentNominationPack): Observable<void> {
        return this.http.put<void>(`${this.apiUrl}/${shipmentId}/nominations`, nominationPack);
    }

    graduateJobToDelivery(shipmentId: number | undefined, deliveryId: number) {
        return this.http.put<ShipmentDelivery>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/graduate-to-delivery`, {});
    }

    getDeliveryById(shipmentId: number | undefined, deliveryId: number) {
        return this.http.get<ShipmentDelivery>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}`);
    }

    getShipmentAndDeliveryByDeliveryId(deliveryId: number, cultureCode: string = 'en-US'): Observable<Shipment> {
        const params = new HttpParams()
            .set('isDriverAndVehicleNeeded', 'true')
            .set('isPayloadItemsNeeded', 'true')
            .set('isNominationsNeeded', 'true')
            .set('isOrderActionDataNeeded', 'true')
            .set('isFilesNeeded', 'true')
            .set('cultureCode', cultureCode);
        return this.http.get<Shipment>(`${this.apiUrl}/deliveries/order/delivery/${deliveryId}`, { params });
    }

    generateDeliveriesFromDrivers(shipmentId: number, selectedDrivers: Driver[]) {
        return this.http.post<ShipmentDelivery[]>(`${this.apiUrl}/${shipmentId}/generate-jobs-from-drivers`, selectedDrivers);
    }

    generateDeliveriesFromAssignments(shipmentId: number, assignments: any[]) {
        return this.http.post<ShipmentDelivery[]>(`${this.apiUrl}/${shipmentId}/generate-jobs-from-assignments`, assignments);
    }

    getShipmentsByProject(projectId: number): Observable<Shipment[]> {
        return this.http.get<Shipment[]>(`${this.apiUrl}/project/${projectId}`);
    }

    getDeliveryTrackingEta(deliveryId: number): Observable<any> {
        return this.http.get<any>(`${this.apiUrl}/deliveries/tracking/${deliveryId}/eta`);
    }

    optimizeWaypoints(shipmentId: number): Observable<ShipmentWaypoint[]> {
        return this.http.post<ShipmentWaypoint[]>(`${this.apiUrl}/${shipmentId}/optimize-waypoints`, {});
    }

    getTripCostSummary(shipmentId: number): Observable<TripCostSummary> {
        return this.http.get<TripCostSummary>(`${this.apiUrl}/${shipmentId}/cost-summary`);
    }

    getShipmentRemark(shipmentId: number): Observable<ShipmentRemark> {
        return this.http.get<ShipmentRemark>(`${this.apiUrl}/${shipmentId}/remarks`);
    }

    getShipmentRemarksFeed(shipmentId: number): Observable<ShipmentRemarksFeed> {
        return this.http.get<ShipmentRemarksFeed>(`${this.apiUrl}/${shipmentId}/remarks/feed`);
    }

    getShipmentWaybills(shipmentId: number): Observable<ShipmentWaybillSummary> {
        return this.http.get<ShipmentWaybillSummary>(`${this.apiUrl}/${shipmentId}/waybills`);
    }

    saveShipmentRemark(shipmentId: number, remark: ShipmentRemark): Observable<ShipmentRemark> {
        return this.http.put<ShipmentRemark>(`${this.apiUrl}/${shipmentId}/remarks`, remark);
    }

    getDeliveryRemark(shipmentId: number, deliveryId: number): Observable<ShipmentRemark> {
        return this.http.get<ShipmentRemark>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/remarks`);
    }

    getDeliveryRemarksFeed(shipmentId: number, deliveryId: number): Observable<ShipmentRemarksFeed> {
        return this.http.get<ShipmentRemarksFeed>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/remarks/feed`);
    }

    getDeliveryWaybills(shipmentId: number, deliveryId: number): Observable<ShipmentWaybillSummary> {
        return this.http.get<ShipmentWaybillSummary>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/waybills`);
    }

    saveHistoricalWaybillNumber(shipmentId: number, deliveryId: number, stopId: number,
        request: { waybillNumber: string; attachmentId: number; expectedWaybillNumber: string | null; confirmReplacement: boolean }) {
        return this.http.put<{ stopId: number; waybillNumber: string; deliveryWaybillNumber: string | null }>(
            `${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/stops/${stopId}/historical-waybill-number`, request);
    }

    saveDeliveryRemark(shipmentId: number, deliveryId: number, remark: ShipmentRemark): Observable<ShipmentRemark> {
        return this.http.put<ShipmentRemark>(`${this.apiUrl}/${shipmentId}/deliveries/${deliveryId}/remarks`, remark);
    }

}
